/**
 * SupplierAttributionCard — "proof of impact" for the supplier
 * Dashboard. Converts invisible effort (shares) into visible results
 * (enquiries → bookings → events driven). Goal: the supplier
 * immediately thinks *"I should share this more."*
 *
 * Hidden when every stat is 0 (cold start — no empty-state shaming).
 * Fetches once on mount via GET /api/suppliers/me/attribution.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShareNetwork, ChatCircleText, CheckCircle, Confetti, Eye } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Stat({ Icon, value, label, testid }) {
  return (
    <div className="flex items-center gap-2 min-w-0" data-testid={testid}>
      <Icon size={15} weight="bold" className="text-white/50 flex-shrink-0" />
      <span className="text-sm font-black text-white tabular-nums">{value}</span>
      <span className="text-[11px] font-bold text-white/50 truncate">{label}</span>
    </div>
  );
}

export default function SupplierAttributionCard({ supplierId, onShareFayre }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/suppliers/me/attribution`)
      .then((r) => { if (!cancelled) setStats(r.data?.stats || null); })
      .catch(() => { if (!cancelled) setStats(null); });
    return () => { cancelled = true; };
  }, []);

  if (!stats) return null;
  const total = (stats.enquiries || 0) + (stats.bookings || 0) + (stats.events_driven || 0) + (stats.share_views || 0);
  if (total === 0) return null;

  const cap = (n) => (n > 99 ? '99+' : n);

  const handleShare = async () => {
    if (onShareFayre) { onShareFayre(); return; }
    // Fallback: share the supplier's directory URL when no fayre
    // context was passed in.
    const url = `${window.location.origin}/suppliers/${supplierId || ''}`;
    try {
      if (navigator.share) await navigator.share({ title: 'Find me on EventDrop', url });
      else if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(url); toast.success('Link copied'); }
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error('Could not share');
    }
  };

  return (
    <div
      className="rounded-2xl p-4 mb-6 border"
      style={{
        background: 'linear-gradient(135deg, rgba(194,157,82,0.08), rgba(184,151,98,0.05))',
        borderColor: 'rgba(255,255,255,0.08)',
      }}
      data-testid="supplier-attribution-card"
    >
      <p className="text-[10px] font-black tracking-[2.5px] uppercase text-[#C29D52]/85 mb-3 flex items-center gap-1.5">
        📣 Your impact this month
      </p>
      <div className="grid grid-cols-2 gap-y-2.5 gap-x-4 mb-4">
        <Stat Icon={ChatCircleText} value={cap(stats.enquiries)}     label={`enquir${stats.enquiries === 1 ? 'y' : 'ies'}`}       testid="attrib-stat-enquiries" />
        <Stat Icon={CheckCircle}    value={cap(stats.bookings)}      label={`booking${stats.bookings === 1 ? '' : 's'}`}           testid="attrib-stat-bookings" />
        <Stat Icon={Confetti}       value={cap(stats.events_driven)} label={`event${stats.events_driven === 1 ? '' : 's'} driven`} testid="attrib-stat-events" />
        <Stat Icon={Eye}            value={cap(stats.share_views)}   label={`view${stats.share_views === 1 ? '' : 's'}`}           testid="attrib-stat-views" />
      </div>
      <button
        onClick={handleShare}
        className="w-full py-2.5 rounded-xl text-xs font-black text-white flex items-center justify-center gap-2 transition-transform active:scale-[0.99]"
        style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
        data-testid="attrib-share-btn"
      >
        <ShareNetwork size={13} weight="bold" />
        Share your fayre link
      </button>
      <p className="text-[10px] text-center text-white/40 mt-2" data-testid="attrib-microcopy">
        Suppliers who share get more enquiries
      </p>
    </div>
  );
}
